import React, { useEffect, useRef, useState, useCallback } from 'react';
import gsap from 'gsap';
import ExperimentNav from '../../components/ExperimentNav';
import { experiments } from '../../App';
import './BranchVeins.css';

const DIARY = `Today the rain came in slowly. The branches outside my window kept growing closer to the glass — closer, then closer still. Even as I wrote this, a leaf landed quietly on the page. I let it stay. I wanted to see how far the vines would reach before the paragraph was gone, before the room was nothing but a green and tangled noise. Touch one to clear it. The page will come back, line by line.`;

// L-system growth parameters
const MAX_DEPTH = 5;
const ROOT_LENGTH_MIN = 110;
const ROOT_LENGTH_MAX = 180;
const CHILD_SHRINK = 0.7;
const ANGLE_SPREAD = 0.55;     // radians (~31°)
const ANGLE_JITTER = 0.4;
const GROWTH_PER_TICK = 0.018; // 0..1 per 40ms tick (~14fps growth)
const SPAWN_MS = 5000;
const TICK_MS = 40;

// Overlap influence radius (px) — distance from a branch sample point to a
// word's center under which the word becomes blurred.
const OVERLAP_RADIUS = 64;
const MAX_BLUR_PX = 3;
const MIN_OPACITY = 0.2;

interface Segment {
  x0: number;
  y0: number;
  angle: number;        // radians
  maxLen: number;
  growth: number;       // 0..1
  depth: number;
  branched: boolean;
  children: Segment[];
}

interface Branch {
  id: number;
  root: Segment;
  bornAt: number;       // performance.now()
}

let nextBranchId = 1;

const rand = (a: number, b: number) => a + Math.random() * (b - a);

function spawnBranch(w: number, h: number): Branch {
  // Pick a random edge, position on that edge, and aim inward.
  const edge = Math.floor(Math.random() * 4);
  let x0 = 0, y0 = 0, angle = 0;
  switch (edge) {
    case 0: // top
      x0 = rand(40, w - 40); y0 = 0; angle = rand(Math.PI * 0.25, Math.PI * 0.75); break;
    case 1: // right
      x0 = w; y0 = rand(40, h - 40); angle = rand(Math.PI * 0.75, Math.PI * 1.25); break;
    case 2: // bottom
      x0 = rand(40, w - 40); y0 = h; angle = rand(Math.PI * 1.25, Math.PI * 1.75); break;
    default: // left
      x0 = 0; y0 = rand(40, h - 40); angle = rand(-Math.PI * 0.25, Math.PI * 0.25); break;
  }
  const root: Segment = {
    x0, y0, angle,
    maxLen: rand(ROOT_LENGTH_MIN, ROOT_LENGTH_MAX),
    growth: 0,
    depth: 0,
    branched: false,
    children: [],
  };
  return { id: nextBranchId++, root, bornAt: performance.now() };
}

function tickGrowth(seg: Segment) {
  if (seg.growth < 1) {
    seg.growth = Math.min(1, seg.growth + GROWTH_PER_TICK);
  } else if (!seg.branched && seg.depth < MAX_DEPTH) {
    seg.branched = true;
    const ex = seg.x0 + Math.cos(seg.angle) * seg.maxLen;
    const ey = seg.y0 + Math.sin(seg.angle) * seg.maxLen;
    const j1 = (Math.random() - 0.5) * ANGLE_JITTER;
    const j2 = (Math.random() - 0.5) * ANGLE_JITTER;
    seg.children.push({
      x0: ex, y0: ey,
      angle: seg.angle - ANGLE_SPREAD + j1,
      maxLen: seg.maxLen * CHILD_SHRINK,
      growth: 0,
      depth: seg.depth + 1,
      branched: false,
      children: [],
    });
    seg.children.push({
      x0: ex, y0: ey,
      angle: seg.angle + ANGLE_SPREAD + j2,
      maxLen: seg.maxLen * CHILD_SHRINK,
      growth: 0,
      depth: seg.depth + 1,
      branched: false,
      children: [],
    });
  }
  for (const c of seg.children) tickGrowth(c);
}

function pathDataFor(seg: Segment): string {
  const ex = seg.x0 + Math.cos(seg.angle) * seg.maxLen * seg.growth;
  const ey = seg.y0 + Math.sin(seg.angle) * seg.maxLen * seg.growth;
  let d = `M ${seg.x0.toFixed(1)} ${seg.y0.toFixed(1)} L ${ex.toFixed(1)} ${ey.toFixed(1)}`;
  for (const c of seg.children) d += ' ' + pathDataFor(c);
  return d;
}

function collectSamples(seg: Segment, out: Array<[number, number]>) {
  const ex = seg.x0 + Math.cos(seg.angle) * seg.maxLen * seg.growth;
  const ey = seg.y0 + Math.sin(seg.angle) * seg.maxLen * seg.growth;
  // Sample start, midpoint, end of this segment
  out.push([seg.x0, seg.y0]);
  out.push([(seg.x0 + ex) / 2, (seg.y0 + ey) / 2]);
  out.push([ex, ey]);
  for (const c of seg.children) collectSamples(c, out);
}

const BranchVeins: React.FC = () => {
  const wrapRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const textRef = useRef<HTMLDivElement>(null);
  const branchesRef = useRef<Branch[]>([]);
  const wordRectsRef = useRef<Array<{ x: number; y: number }>>([]);
  const tickTimerRef = useRef<number | null>(null);
  const spawnTimerRef = useRef<number | null>(null);
  const startTimeRef = useRef<number>(performance.now());

  const [renderTick, setRenderTick] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const [branchCount, setBranchCount] = useState(0);
  const [viewport, setViewport] = useState(() => ({
    w: typeof window !== 'undefined' ? window.innerWidth : 1200,
    h: typeof window !== 'undefined' ? window.innerHeight : 800,
  }));

  const exp = experiments.find(e => e.id === 'branch-veins');
  const words = DIARY.split(/\s+/).filter(Boolean);

  // ── Compute per-word centers in viewport coords ─────────
  const measureWords = useCallback(() => {
    const text = textRef.current;
    if (!text) return;
    const spans = text.querySelectorAll<HTMLSpanElement>('.veinsWord');
    const rects: Array<{ x: number; y: number }> = [];
    spans.forEach(sp => {
      const r = sp.getBoundingClientRect();
      rects.push({ x: r.left + r.width / 2, y: r.top + r.height / 2 });
    });
    wordRectsRef.current = rects;
  }, []);

  // ── Update word blur/opacity from branch overlap ────────
  const updateOverlap = useCallback(() => {
    const text = textRef.current;
    if (!text) return;

    // Collect sample points from all currently-growing branches
    const samples: Array<[number, number]> = [];
    for (const b of branchesRef.current) collectSamples(b.root, samples);

    const spans = text.querySelectorAll<HTMLSpanElement>('.veinsWord');
    const rects = wordRectsRef.current;
    spans.forEach((sp, i) => {
      // Skip if currently animating via GSAP (avoid override)
      if (sp.dataset.locked === '1') return;

      const r = rects[i];
      if (!r) return;
      let minD2 = Infinity;
      for (const [sx, sy] of samples) {
        const dx = sx - r.x;
        const dy = sy - r.y;
        const d2 = dx * dx + dy * dy;
        if (d2 < minD2) minD2 = d2;
      }
      const d = Math.sqrt(minD2);
      const t = Math.max(0, 1 - d / OVERLAP_RADIUS);
      const blur = t * MAX_BLUR_PX;
      const opacity = 1 - t * (1 - MIN_OPACITY);
      sp.style.filter = blur > 0.05 ? `blur(${blur.toFixed(2)}px)` : '';
      sp.style.opacity = String(opacity.toFixed(2));
    });
  }, []);

  // ── Tick: grow + re-render + update overlap ─────────────
  useEffect(() => {
    startTimeRef.current = performance.now();
    measureWords();

    const tick = () => {
      for (const b of branchesRef.current) tickGrowth(b.root);
      setRenderTick(t => t + 1);
      setElapsed(Math.floor((performance.now() - startTimeRef.current) / 1000));
      // updateOverlap runs after React commit (in another effect below)
    };
    tickTimerRef.current = window.setInterval(tick, TICK_MS);

    const onResize = () => {
      setViewport({ w: window.innerWidth, h: window.innerHeight });
      measureWords();
    };
    window.addEventListener('resize', onResize);

    return () => {
      if (tickTimerRef.current) clearInterval(tickTimerRef.current);
      window.removeEventListener('resize', onResize);
    };
  }, [measureWords]);

  // ── After each render, refresh overlap state ─────────────
  useEffect(() => {
    updateOverlap();
    setBranchCount(branchesRef.current.length);
  }, [renderTick, updateOverlap]);

  // ── Spawn loop ──────────────────────────────────────────
  useEffect(() => {
    const spawn = () => {
      const w = window.innerWidth;
      const h = window.innerHeight;
      branchesRef.current.push(spawnBranch(w, h));
      setRenderTick(t => t + 1);
    };
    // First branch shortly after mount so users see motion fast
    const firstTimer = window.setTimeout(spawn, 1200);
    spawnTimerRef.current = window.setInterval(spawn, SPAWN_MS);
    return () => {
      clearTimeout(firstTimer);
      if (spawnTimerRef.current) clearInterval(spawnTimerRef.current);
    };
  }, []);

  // ── Click a branch — GSAP shrink + remove + restore text ─
  const onBranchClick = useCallback((e: React.MouseEvent<SVGGElement>, branchId: number) => {
    e.stopPropagation();
    const g = e.currentTarget;

    // Compute which words this branch was affecting BEFORE removing it,
    // so we can GSAP-restore them explicitly per spec.
    const branch = branchesRef.current.find(b => b.id === branchId);
    if (!branch) return;
    const samples: Array<[number, number]> = [];
    collectSamples(branch.root, samples);

    const text = textRef.current;
    const affected: HTMLSpanElement[] = [];
    if (text) {
      const spans = text.querySelectorAll<HTMLSpanElement>('.veinsWord');
      const rects = wordRectsRef.current;
      spans.forEach((sp, i) => {
        const r = rects[i];
        if (!r) return;
        for (const [sx, sy] of samples) {
          const dx = sx - r.x;
          const dy = sy - r.y;
          if (dx * dx + dy * dy < OVERLAP_RADIUS * OVERLAP_RADIUS) {
            affected.push(sp);
            break;
          }
        }
      });
    }

    // Lock so the rAF-ish overlap loop doesn't fight GSAP
    affected.forEach(sp => { sp.dataset.locked = '1'; });

    // GSAP: shrink the branch to scale 0 over 0.6s ease-out
    gsap.to(g, {
      scale: 0,
      duration: 0.6,
      ease: 'power2.out',
      onComplete: () => {
        branchesRef.current = branchesRef.current.filter(b => b.id !== branchId);
        setRenderTick(t => t + 1);
      },
    });

    // GSAP: smoothly restore blur and opacity on affected words
    gsap.to(affected, {
      filter: 'blur(0px)',
      opacity: 1,
      duration: 0.55,
      ease: 'power2.out',
      onComplete: () => {
        affected.forEach(sp => {
          sp.style.filter = '';
          delete sp.dataset.locked;
        });
      },
    });
  }, []);

  return (
    <div className="branchVeins" ref={wrapRef}>
      <ExperimentNav prompt={exp?.prompt} title={exp?.title} />

      {/* Diary text — measured once on layout, re-measured on resize */}
      <div className="veinsTextWrap">
        <div className="veinsText" ref={textRef}>
          {words.map((w, i) => (
            <span key={i} className="veinsWord">{w}</span>
          ))}
        </div>
      </div>

      {/* SVG branches layer */}
      <svg ref={svgRef} className="veinsSvg"
           viewBox={`0 0 ${viewport.w} ${viewport.h}`}
           preserveAspectRatio="none">
        {branchesRef.current.map(b => {
          const d = pathDataFor(b.root);
          return (
            <g key={b.id} className="branch"
               style={{ transformOrigin: `${b.root.x0}px ${b.root.y0}px` }}
               onClick={(e) => onBranchClick(e, b.id)}>
              <path className="hit" d={d} />
              <path className="line" d={d} />
            </g>
          );
        })}
      </svg>

      <div className="veinsHint">▸ click a branch to clear it</div>

      <div className="veinsHud">
        <span>elapsed <span className="hudValue">{elapsed}s</span></span>
        <span>branches <span className="hudValue">{branchCount}</span></span>
        <span>next <span className="hudValue">{Math.max(0, SPAWN_MS / 1000 - (elapsed % (SPAWN_MS / 1000)))}s</span></span>
      </div>
    </div>
  );
};

export default BranchVeins;
